import {
  IonContent,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonSpinner,
  IonListHeader,
} from '@ionic/react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { GET_TEAM } from '../../../graphql/queries/getTeam';
import type { GetTeamQuery, GetTeamQueryVariables, Player } from '../../../types/graphql-generated';
import { PlayerRoleEnum } from '../../../types/graphql-generated';
import { PlayerCard } from '../../fantasy-leagues-tabs/components/PlayerCard';
import { AppSectionHeader } from '../../components/ui/AppSectionHeader';

const ROLE_LABELS: Record<PlayerRoleEnum, string> = {
  [PlayerRoleEnum.Portero]: 'Porteros',
  [PlayerRoleEnum.Defensa]: 'Defensas',
  [PlayerRoleEnum.Centrocampista]: 'Centrocampistas',
  [PlayerRoleEnum.Delantero]: 'Delanteros',
};

const ROLE_ORDER: PlayerRoleEnum[] = [
  PlayerRoleEnum.Portero,
  PlayerRoleEnum.Defensa,
  PlayerRoleEnum.Centrocampista,
  PlayerRoleEnum.Delantero,
];

const Roster: React.FC = () => {
  const teamId = useParams<'teamId'>().teamId!;

  const { data, loading, error } = useQuery<GetTeamQuery, GetTeamQueryVariables>(GET_TEAM, {
    variables: { teamId },
  });

  const team = data?.getTeam;

  // Group players by role
  type TeamPlayer = NonNullable<NonNullable<typeof team>['players']>[number];
  const playersByRole: Record<PlayerRoleEnum, TeamPlayer[]> = {
    [PlayerRoleEnum.Portero]: [],
    [PlayerRoleEnum.Defensa]: [],
    [PlayerRoleEnum.Centrocampista]: [],
    [PlayerRoleEnum.Delantero]: [],
  };

  [...(team?.players ?? [])].sort((a, b) => (b.marketValue ?? 0) - (a.marketValue ?? 0)).forEach((player: TeamPlayer) => {
    if (player.role) {
      playersByRole[player.role as PlayerRoleEnum].push(player);
    }
  });

  return (
    <IonPage>
      <AppSectionHeader variant="main">
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/app/main/competition" />
          </IonButtons>
          <IonTitle className="ml-4">{team?.nameShow || 'Plantilla'}</IonTitle>
        </IonToolbar>
      </AppSectionHeader>
      <IonContent>
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '32px' }}>
            <IonSpinner />
          </div>
        )}

        {error && (
          <div style={{ padding: '16px', textAlign: 'center', color: 'red' }}>
            Error al cargar la plantilla: {error.message}
          </div>
        )}

        {team && (
          <div style={{ padding: '16px' }}>
            {ROLE_ORDER.map((role) => {
              const players = playersByRole[role];
              if (players.length === 0) return null;

              return (
                <div key={role} style={{ marginBottom: '24px' }}>
                  <IonListHeader>
                    <h2>{ROLE_LABELS[role]}</h2>
                  </IonListHeader>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {players.map((player: typeof players[number]) => (
                      <PlayerCard key={player.id} player={player as Player} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default Roster;
